/**
 * Membership Service
 *
 * Provides functionality for working with membership data.
 */

import { apiClient } from '../../core/api/client';
import { ApiService } from '../../core/api/service';
import {
    ApiResponse,
    MemCountsResponse,
    MemCreateMembersRequest,
    MemCreateMembersResponse, QueryBusinessInfoRequest,
    QueryBusinessInfoResponse
} from "../../../model";
import {
    MembershipGetEventResponseItem,
    MemCreateCertificationsRequest,
    MemCreateCertificationsResponse,
    MemCreateCustomProductsRequest,
    MemCreateCustomProductsResponse,
    MemCreateDelegatesRequest,
    MemCreateDelegatesResponse,
    MemCreateProductsRequest,
    MemCreateProductsResponse,
    MemDeleteCertificationsRequest,
    MemDeleteCertificationsResponse,
    MemDeleteCustomProductsRequest,
    MemDeleteCustomProductsResponse,
    MemDeleteDelegatesRequest,
    MemDeleteDelegatesResponse,
    MemDeleteProductsRequest,
    MemDeleteProductsResponse,
    MemGetCertificationsRequest,
    MemGetCertificationsResponse,
    MemGetCustomProductsRequest,
    MemGetCustomProductsResponse,
    MemGetDdelegatesRequest,
    MemGetDdelegatesResponse, 
    MemGetMemberRequest, 
    MemGetMemberResponse,
    MemGetProductsRequest,
    MemGetProductsResponse, 
    MemberUpdateItem,
    MemSearchRequest, 
    MemSearchResponse,
    MemUpdateCertificationsRequest,
    MemUpdateCertificationsResponse,
    MemUpdateCustomProductsRequest,
    MemUpdateCustomProductsResponse,
    MemUpdateDelegatesRequest,
    MemUpdateDelegatesResponse,
    MemUpdateMembersRequest,
    MemUpdateMembersResponse,
    MemUpdateProductsRequest,
    MemUpdateProductsResponse
} from "../../../model";

const singleLineMemberFields: Array<keyof MemberUpdateItem> = [
    'memberId',
    'companyName',
    'contactZip',
    'contactAddress',
    'companyPhone',
    'companyFax',
    'factoryZip',
    'factoryAddress',
    'factoryPhone',
    'factoryFax',
    'chairman',
    'generalManager',
    'responsiblePerson',
    'collectionAgent',
    'companyNameEn',
    'contactAddressEn',
    'factoryAddressEn',
    'responsiblePersonEn',
    'chairmanEn',
    'generalManagerEn',
    'membershipContact',
    'membershipContact_2',
    'committeeContact',
    'exhibitionContact'
];

const sanitizeMemberSingleLineFields = (member: MemberUpdateItem): MemberUpdateItem => {
    const sanitizedMember = {...member};

    singleLineMemberFields.forEach(field => {
        const value = sanitizedMember[field];
        if (typeof value === 'string') {
            (sanitizedMember as Record<string, unknown>)[field] = value
                .replace(/[\r\n\t\f]+/g, ' ')
                .trim();
        }
    });

    return sanitizedMember;
};

export class MembershipService extends ApiService {
    /**
     * Search memberships based on criteria
     */
    async search(request: MemSearchRequest): Promise<MemSearchResponse[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemSearchResponse[]>>(
                '/api/membership/search',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * 查詢公司基本資料
     */
    async getBusinessInfo(request: QueryBusinessInfoRequest): Promise<QueryBusinessInfoResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<QueryBusinessInfoResponse>>(
                '/api/base-data/queryBusinessInfo',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get member details by ID, member ID, or business ID
     */
    async getMember(
        request: MemGetMemberRequest
    ): Promise<MemGetMemberResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemGetMemberResponse>>(
                '/api/membership/getMember',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get delegates for a member
     */
    async getDelegates(
        request: MemGetDdelegatesRequest
    ): Promise<MemGetDdelegatesResponse[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemGetDdelegatesResponse[]>>(
                '/api/membership/getDdelegates',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get products for a member
     */
    async getProducts(
        request: MemGetProductsRequest
    ): Promise<MemGetProductsResponse[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemGetProductsResponse[]>>(
                '/api/membership/getProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get custom products for a member
     */
    async getCustomProducts(
        request: MemGetCustomProductsRequest
    ): Promise<MemGetCustomProductsResponse[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemGetCustomProductsResponse[]>>(
                '/api/membership/getCustomProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get certifications for a member
     */
    async getCertifications(
        request: MemGetCertificationsRequest
    ): Promise<MemGetCertificationsResponse[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemGetCertificationsResponse[]>>(
                '/api/membership/getCertifications',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Get events for a member
     */
    async getEvents(
        membershipId: number
    ): Promise<MembershipGetEventResponseItem[] | null> {
        try {
            const response = await apiClient.post<ApiResponse<MembershipGetEventResponseItem[]>>(
                '/api/membership/getEvents',
                { membershipId }
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Update a member's information
     */
    async updateMember(
        request: MemUpdateMembersRequest
    ): Promise<MemUpdateMembersResponse | null> {
        try {
            const sanitizedRequest = {
                ...request,
                members: request.members.map(sanitizeMemberSingleLineFields)
            };
            const response = await apiClient.post<ApiResponse<MemUpdateMembersResponse>>(
                '/api/membership/updateMemberns',
                sanitizedRequest
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    async createMember(
        request: MemCreateMembersRequest
    ): Promise<MemCreateMembersResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemCreateMembersResponse>>(
                '/api/membership/createMembers',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Create delegates for a member
     */
    async createDelegates(
        request: MemCreateDelegatesRequest
    ): Promise<MemCreateDelegatesResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemCreateDelegatesResponse>>(
                '/api/membership/createDelegates',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Update delegates for a member
     */
    async updateDelegates(
        request: MemUpdateDelegatesRequest
    ): Promise<MemUpdateDelegatesResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemUpdateDelegatesResponse>>(
                '/api/membership/updateDelegates',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Delete delegates for a member
     */
    async deleteDelegates(
        request: MemDeleteDelegatesRequest
    ): Promise<MemDeleteDelegatesResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemDeleteDelegatesResponse>>(
                '/api/membership/deleteDelegates',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Create products for a member
     */
    async createProducts(
        request: MemCreateProductsRequest
    ): Promise<MemCreateProductsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemCreateProductsResponse>>(
                '/api/membership/createProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Update products for a member
     */
    async updateProducts(
        request: MemUpdateProductsRequest
    ): Promise<MemUpdateProductsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemUpdateProductsResponse>>(
                '/api/membership/updateProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Delete products for a member
     */
    async deleteProducts(
        request: MemDeleteProductsRequest
    ): Promise<MemDeleteProductsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemDeleteProductsResponse>>(
                '/api/membership/deleteProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Create custom products for a member
     */
    async createCustomProducts(
        request: MemCreateCustomProductsRequest
    ): Promise<MemCreateCustomProductsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemCreateCustomProductsResponse>>(
                '/api/membership/createCustomProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Update custom products for a member
     */
    async updateCustomProducts(
        request: MemUpdateCustomProductsRequest
    ): Promise<MemUpdateCustomProductsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemUpdateCustomProductsResponse>>(
                '/api/membership/updateCustomProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Delete custom products for a member
     */
    async deleteCustomProducts(
        request: MemDeleteCustomProductsRequest
    ): Promise<MemDeleteCustomProductsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemDeleteCustomProductsResponse>>(
                '/api/membership/deleteCustomProducts',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Create certifications for a member
     */
    async createCertifications(
        request: MemCreateCertificationsRequest
    ): Promise<MemCreateCertificationsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemCreateCertificationsResponse>>(
                '/api/membership/createCertifications',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Update certifications for a member
     */
    async updateCertifications(
        request: MemUpdateCertificationsRequest
    ): Promise<MemUpdateCertificationsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemUpdateCertificationsResponse>>(
                '/api/membership/updateCertifications',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
     * Delete certifications for a member
     */
    async deleteCertifications(
        request: MemDeleteCertificationsRequest
    ): Promise<MemDeleteCertificationsResponse | null> {
        try {
            const response = await apiClient.post<ApiResponse<MemDeleteCertificationsResponse>>(
                '/api/membership/deleteCertifications',
                request
            );

            return this.processResponse(response);
        } catch (error) {
            return this.handleError(error);
        }
    }

    /**
    * Get current member counts
     */
    async getMemberCounts(
    ): Promise<MemCountsResponse | null> {
        try {
            const response = await apiClient.get<ApiResponse<MemCountsResponse>>(
                '/api/membership/counts'  // 根據您的後端路由調整
            );

            const result = this.processResponse<MemCountsResponse>(response);
            return result;
        } catch (error) {
            return this.handleError(error);
        }
    }
}

// Export a singleton instance
export const membershipService = new MembershipService();
